import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";
import "./paymentSettings.css";

const PaymentSettings = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    stripeEnabled: false,
    stripePublishableKey: "",
    stripeSuccessUrl: "",
    stripeCancelUrl: "",
    stripeWebhookUrl: "",
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_BILLING_PAYMENT_SETTINGS);
      if (response.data.status && response.data.data) {
        const data = response.data.data;
        setSettings({
          stripeEnabled: data.stripeEnabled ?? false,
          stripePublishableKey: data.stripePublishableKey || "",
          stripeSuccessUrl: data.stripeSuccessUrl || "",
          stripeCancelUrl: data.stripeCancelUrl || "",
          stripeWebhookUrl: data.stripeWebhookUrl || "",
        });
      }
    } catch {
      showErrorToast("Failed to load payment settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveButtonStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
    }),
    [themeColor]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axiosInstance.put(APIs.PUT_UPDATE_BILLING_PAYMENT_SETTINGS, {
        settingsID: 1,
        stripeEnabled: settings.stripeEnabled,
        stripePublishableKey: settings.stripePublishableKey,
        stripeSuccessUrl: settings.stripeSuccessUrl,
        stripeCancelUrl: settings.stripeCancelUrl,
      });
      showSuccessToast("Payment settings saved");
      fetchSettings();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const copyWebhookUrl = () => {
    if (!settings.stripeWebhookUrl) return;
    navigator.clipboard.writeText(settings.stripeWebhookUrl);
    showSuccessToast("Webhook URL copied");
  };

  const publishableKeyPreview = settings.stripePublishableKey
    ? `${settings.stripePublishableKey.slice(0, 12)}…`
    : "Not set";

  return (
    <div className={`bps-settings-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Payment Settings</li>
          </ol>
          <h1 className="page-header mb-0">Billing Payment Settings</h1>
          <p className="bps-page-desc mb-0 mt-2">
            Configure Stripe Checkout for BackOffice invoice payments. Secret key and webhook secret are stored in server appsettings only.
          </p>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div
            className="bps-info-banner"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              boxShadow: `0 4px 20px ${themeColor}40`,
            }}
          >
            <div className="bps-info-icon">
              <i className="fa fa-credit-card" aria-hidden="true" />
            </div>
            <div>
              <div className="bps-info-title">Stripe Checkout for BackOffice billing</div>
              <p className="bps-info-text">
                Publishable key and redirect URLs are saved here. The secret key and webhook signing secret must be configured on the API server only.
              </p>
              <div className="bps-summary-pills">
                <span className="bps-pill">{settings.stripeEnabled ? "Stripe enabled" : "Stripe disabled"}</span>
                <span className="bps-pill">Key: {publishableKeyPreview}</span>
                <span className="bps-pill">
                  {settings.stripeWebhookUrl ? "Webhook URL ready" : "Webhook URL pending"}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSave}>
            <div className="bps-section">
              <div className="bps-section-head">
                <span className="bps-section-icon">
                  <i className="fa fa-shopping-cart" aria-hidden="true" />
                </span>
                <h2 className="bps-section-title">Stripe Checkout</h2>
              </div>
              <div className="bps-section-body">
                <div className="bps-toggle-row">
                  <div>
                    <div className="bps-toggle-label">Enable Stripe card payments in BackOffice</div>
                    <div className="bps-help mb-0">
                      When off, invoice card payments are hidden in BackOffice regardless of key configuration.
                    </div>
                  </div>
                  <ToggleSwitch
                    isChecked={settings.stripeEnabled}
                    onToggle={(e) => setSettings({ ...settings, stripeEnabled: e.target.checked })}
                    name="stripeEnabled"
                  />
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <FormInput
                      label="Stripe publishable key"
                      type="text"
                      placeHolder="pk_live_… or pk_test_…"
                      value={settings.stripePublishableKey}
                      onchange={(e) =>
                        setSettings({ ...settings, stripePublishableKey: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-lg-6">
                    <FormInput
                      label="Success redirect URL (BackOffice)"
                      type="text"
                      placeHolder="https://your-backoffice.com/administration/Billing"
                      value={settings.stripeSuccessUrl}
                      onchange={(e) => setSettings({ ...settings, stripeSuccessUrl: e.target.value })}
                    />
                    <div className="bps-help">
                      Customer returns here after a successful Stripe Checkout payment.
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <FormInput
                      label="Cancel redirect URL (BackOffice)"
                      type="text"
                      placeHolder="https://your-backoffice.com/administration/Billing"
                      value={settings.stripeCancelUrl}
                      onchange={(e) => setSettings({ ...settings, stripeCancelUrl: e.target.value })}
                    />
                    <div className="bps-help">Customer returns here if they cancel checkout.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bps-section">
              <div className="bps-section-head">
                <span className="bps-section-icon">
                  <i className="fa fa-link" aria-hidden="true" />
                </span>
                <h2 className="bps-section-title">Stripe webhook</h2>
              </div>
              <div className="bps-section-body">
                <p className="bps-help mb-3">
                  Add this endpoint in the Stripe Dashboard for event{" "}
                  <strong>checkout.session.completed</strong>. Configure{" "}
                  <code className="bps-code">Stripe:WebhookSecret</code> in API appsettings.
                </p>
                <div className="bps-webhook-row">
                  <input
                    type="text"
                    className="bps-webhook-input"
                    readOnly
                    value={settings.stripeWebhookUrl}
                    placeholder="Webhook URL will appear after settings load"
                  />
                  <button
                    type="button"
                    className="bps-copy-btn"
                    onClick={copyWebhookUrl}
                    disabled={!settings.stripeWebhookUrl}
                  >
                    <i className="fa fa-copy me-1" aria-hidden="true" />
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div className="bps-save-row">
              <div className="bps-save-note">Redirect URLs and publishable key are stored in SuperAdmin settings.</div>
              <button type="submit" className="bps-save-btn" style={saveButtonStyle} disabled={loading}>
                <i className="fa fa-save me-2" aria-hidden="true" />
                Save settings
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
